import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, CheckCircle, AlertTriangle, Lock } from 'lucide-react';

const BACKEND_URL = ''; // same-origin: reverse proxy routes /api/* to backend

const ActivateAccount = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [state, setState] = useState('loading'); // loading | form | success | error
  const [customerInfo, setCustomerInfo] = useState({});
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [kvkkConsent, setKvkkConsent] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setState('error');
      setErrorMsg('Geçersiz aktivasyon bağlantısı. Token bulunamadı.');
      return;
    }
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/activate/verify?token=${token}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Geçersiz veya süresi dolmuş bağlantı');
      }
      const data = await res.json();
      setCustomerInfo(data);
      setState('form');
    } catch (err) {
      setState('error');
      setErrorMsg(err.message);
    }
  };

  const handleActivate = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setErrorMsg('Şifre en az 6 karakter olmalıdır.');
      return;
    }
    if (password !== confirmPassword) {
      setErrorMsg('Şifreler eşleşmiyor.');
      return;
    }
    if (!kvkkConsent) {
      setErrorMsg('Hesabınızı aktifleştirmek için KVKK onayı vermeniz gerekiyor.');
      return;
    }

    setSubmitting(true);
    setErrorMsg('');

    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password, kvkkConsent }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Aktivasyon başarısız');
      }
      setState('success');
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
      <Card className="bg-gray-800 border-gray-700 w-full max-w-md">
        <CardContent className="p-8">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-brand">ART Garage</h1>
            <p className="text-gray-400 text-sm mt-1">Müşteri Portal Aktivasyonu</p>
          </div>

          {state === 'loading' && (
            <div className="text-center py-8" data-testid="activation-loading">
              <Loader2 className="h-8 w-8 animate-spin text-brand mx-auto" />
              <p className="text-gray-400 mt-3">Bağlantı doğrulanıyor...</p>
            </div>
          )}

          {state === 'error' && (
            <div className="text-center py-8" data-testid="activation-error">
              <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-3" />
              <h2 className="text-white text-lg font-semibold mb-2">Aktivasyon Hatası</h2>
              <p className="text-gray-400 text-sm">{errorMsg}</p>
              <Button onClick={() => navigate('/login')} className="mt-6 bg-gray-700 hover:bg-gray-600 text-white">
                Giriş Sayfasına Dön
              </Button>
            </div>
          )}

          {state === 'form' && (
            <div data-testid="activation-form">
              <div className="bg-gray-700/50 rounded-lg p-4 mb-6 text-center">
                <p className="text-white font-semibold">{customerInfo.name}</p>
                <p className="text-gray-400 text-sm">{customerInfo.email}</p>
              </div>

              <p className="text-gray-300 text-sm mb-4">
                Hesabınızı aktifleştirmek için bir şifre belirleyin.
              </p>

              <form onSubmit={handleActivate} className="space-y-4">
                {errorMsg && (
                  <div className="bg-red-900/40 border border-red-700 rounded-lg p-3 text-red-400 text-sm" data-testid="activation-form-error">
                    {errorMsg}
                  </div>
                )}

                <div>
                  <Label className="text-white text-sm">Şifre</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                    <Input type="password" value={password} onChange={(e) => { setPassword(e.target.value); setErrorMsg(''); }}
                      placeholder="En az 6 karakter" className="bg-gray-900 border-gray-600 text-white pl-10"
                      data-testid="activation-password-input" />
                  </div>
                </div>

                <div>
                  <Label className="text-white text-sm">Şifre Tekrar</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                    <Input type="password" value={confirmPassword} onChange={(e) => { setConfirmPassword(e.target.value); setErrorMsg(''); }}
                      placeholder="Şifrenizi tekrar girin" className="bg-gray-900 border-gray-600 text-white pl-10"
                      data-testid="activation-confirm-password-input" />
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-gray-700/30 rounded-lg p-3 mt-1">
                  <input type="checkbox" checked={kvkkConsent} onChange={(e) => { setKvkkConsent(e.target.checked); setErrorMsg(''); }}
                    className="mt-1 rounded w-4 h-4" data-testid="kvkk-checkbox" />
                  <p className="text-gray-300 text-xs leading-relaxed">
                    Kişisel verilerimin 6698 sayılı KVKK kapsamında ART Garage tarafından işlenmesini ve saklanmasını onaylıyorum. Verilerim hizmet süreçleri, iletişim ve yasal yükümlülükler için kullanılacaktır.
                  </p>
                </div>

                <Button type="submit" disabled={submitting} data-testid="activation-submit-btn"
                  className="w-full bg-brand hover:bg-brand text-white font-semibold py-2.5">
                  {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Aktifleştiriliyor...</> : 'Hesabımı Aktifleştir'}
                </Button>
              </form>
            </div>
          )}

          {state === 'success' && (
            <div className="text-center py-8" data-testid="activation-success">
              <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-3" />
              <h2 className="text-white text-lg font-semibold mb-2">Hesabınız Aktif!</h2>
              <p className="text-gray-400 text-sm">Artık portal üzerinden aracınızın durumunu takip edebilirsiniz.</p>
              <Button onClick={() => navigate('/login')} data-testid="activation-go-login-btn"
                className="mt-6 bg-brand hover:bg-brand text-white">
                Giriş Yap
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ActivateAccount;
